import React from "react";
import { motion } from "framer-motion";
import { ArrowRight, MapPin } from "lucide-react";
import { ASSETS } from "../data/menu";

const Steam = ({ delay = 0, left = "0px" }) => (
  <motion.span
    aria-hidden="true"
    initial={{ y: 0, opacity: 0, scale: 1 }}
    animate={{ y: -160, opacity: [0, 0.5, 0], scale: 1.8 }}
    transition={{ duration: 6, delay, repeat: Infinity, ease: "easeOut" }}
    className="absolute bottom-0 w-10 h-10 rounded-full blur-2xl bg-cream/70"
    style={{ left }}
  />
);

export const Hero = () => {
  return (
    <section
      id="top"
      data-testid="hero-section"
      className="relative min-h-screen w-full overflow-hidden bg-cream"
    >
      {/* Decorative top horizon line */}
      <div className="absolute top-24 left-0 right-0 h-px bg-espresso/10 z-10" />

      <div className="relative max-w-[1400px] mx-auto px-6 md:px-10 pt-32 md:pt-36 pb-20 grid lg:grid-cols-12 gap-10 lg:gap-16 items-center min-h-screen">
        {/* Left: copy */}
        <div className="lg:col-span-6 relative z-20">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
            className="flex items-center gap-3 text-espresso/60 text-[12px] tracking-[0.3em] uppercase mb-6"
          >
            <span className="h-px w-10 bg-espresso/40" />
            Kemps Corner · Mumbai
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1.1, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}
            className="font-serif text-[16vw] sm:text-[12vw] lg:text-[10vw] xl:text-[160px] leading-[0.9] tracking-tight text-espresso text-balance"
            data-testid="hero-brand"
          >
            <span className="block">OROMA</span>
            <span className="block font-script text-gold text-[26vw] sm:text-[14vw] lg:text-[7vw] xl:text-[110px] -mt-4 lg:-mt-6 italic font-normal">
              your daily ritual
            </span>
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.5 }}
            className="mt-8 max-w-md font-serif italic text-2xl md:text-3xl text-espresso/80 leading-snug"
            data-testid="hero-tagline"
          >
            Where coffee becomes an experience.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.75 }}
            className="mt-10 flex flex-wrap gap-4"
          >
            <a
              href="#menu"
              data-testid="hero-cta-view-menu"
              className="group inline-flex items-center gap-3 bg-espresso text-cream px-8 py-4 text-[12px] tracking-[0.28em] uppercase hover:bg-gold hover:text-espresso transition-colors duration-500"
            >
              View Menu
              <ArrowRight size={16} className="transition-transform duration-500 group-hover:translate-x-1" />
            </a>
            <a
              href="#visit"
              data-testid="hero-cta-visit-us"
              className="group inline-flex items-center gap-3 border border-espresso text-espresso px-8 py-4 text-[12px] tracking-[0.28em] uppercase hover:bg-espresso hover:text-cream transition-colors duration-500"
            >
              <MapPin size={14} />
              Visit Us
            </a>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1.2, delay: 1.1 }}
            className="mt-16 flex items-center gap-8 text-espresso/60 text-[11px] tracking-[0.3em] uppercase"
          >
            <span>Est. Mumbai</span>
            <span className="h-px w-6 bg-espresso/30" />
            <span>Slow Coffee</span>
            <span className="h-px w-6 bg-espresso/30" />
            <span>Daily 9–11</span>
          </motion.div>
        </div>

        {/* Right: image with steam */}
        <motion.div
          initial={{ opacity: 0, scale: 0.96 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 1.4, delay: 0.3, ease: [0.22, 1, 0.36, 1] }}
          className="lg:col-span-6 relative"
        >
          <div className="relative aspect-[4/5] w-full max-w-[560px] mx-auto overflow-hidden">
            <img
              src={ASSETS.hero}
              alt="OROMA coffee cup with a fresh croissant"
              className="w-full h-full object-cover"
              data-testid="hero-image"
            />
            {/* steam */}
            <div className="absolute left-1/2 -translate-x-1/2 bottom-1/3 w-32 h-24 pointer-events-none">
              <Steam delay={0} left="20px" />
              <Steam delay={1.5} left="55px" />
              <Steam delay={3} left="85px" />
            </div>
            {/* gold corner accent */}
            <div className="absolute -top-3 -right-3 w-16 h-16 border-t border-r border-gold" />
            <div className="absolute -bottom-3 -left-3 w-16 h-16 border-b border-l border-gold" />
          </div>

          {/* floating script note */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 1, delay: 1.3 }}
            className="hidden md:block absolute -bottom-4 left-2 lg:-left-10 max-w-[260px]"
          >
            <p className="font-script text-3xl text-espresso italic leading-tight">
              "slow mornings, <br /> meaningful sips."
            </p>
          </motion.div>
        </motion.div>
      </div>

      {/* scroll indicator */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1, delay: 1.6 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 text-espresso/50 z-10"
      >
        <span className="text-[10px] tracking-[0.3em] uppercase">Scroll</span>
        <motion.span
          animate={{ y: [0, 8, 0] }}
          transition={{ duration: 1.8, repeat: Infinity, ease: "easeInOut" }}
          className="block w-px h-10 bg-espresso/30"
        />
      </motion.div>
    </section>
  );
};
